import { HeaderPage } from "@/components/HeaderPage";
import { Loading } from "@/components/Loading";
import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OccurrenceDetails } from "@/pages/occurrences/view/index";
import {
  ArrowLeft,
  Edit,
  Clock,
  User,
  Play,
  Pause,
  CheckCircle,
  RotateCcw,
  XCircle,
  Copy,
  Eye,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TextArea } from "@/components/TextArea";
import { FileUpload, type UploadedFile } from "@/components/FileUpload";
import {
  useGetServiceOrderById,
  useGetServiceOrderHistory,
  useStartServiceOrder,
  useSuspendServiceOrder,
  useResumeServiceOrder,
  useCompleteServiceOrder,
  useReturnServiceOrder,
  useCancelServiceOrder,
} from "@/hooks/useServiceOrders";
import { useCreateOccurrenceComment } from "@/hooks/useOccurrences";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import toast from "react-hot-toast";

const priorityLabels: Record<string, { label: string; color: string }> = {
  LOW: { label: "Baixa", color: "bg-blue-500" },
  MEDIUM: { label: "Média", color: "bg-yellow-500" },
  HIGH: { label: "Alta", color: "bg-orange-500" },
  URGENT: { label: "Crítica", color: "bg-red-500" },
};

const statusLabels: Record<string, { label: string; color: string }> = {
  PENDING: { label: "Pendente", color: "bg-gray-500" },
  IN_PROGRESS: { label: "Em Andamento", color: "bg-blue-600" },
  AWAITING_VALIDATION: { label: "Aguardando Validação", color: "bg-amber-500" },
  SUSPENDED: { label: "Suspensa", color: "bg-yellow-600" },
  COMPLETED: { label: "Concluída", color: "bg-green-500" },
  CANCELLED: { label: "Cancelada", color: "bg-red-500" },
};

// Componente de detalhes que pode ser usado em modal ou página
export const ServiceOrderDetails = ({
  idProp,
}: { idProp?: string } = {}) => {
  const { id: routeId } = useParams<{ id: string }>();
  const id = idProp || routeId;
  const navigate = useNavigate();
  const {
    data: serviceOrderData,
    isLoading,
    error,
    refetch,
  } = useGetServiceOrderById(id!);
  const { data: historyData, refetch: refetchHistory } =
    useGetServiceOrderHistory(id!);

    const serviceOrder = serviceOrderData || serviceOrderData?.data;
  const history = historyData?.data || [];

  // Estados para dialogs
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [viewOccurrenceDialogOpen, setViewOccurrenceDialogOpen] = useState(false);
  const [selectedOccurrenceProtocol, setSelectedOccurrenceProtocol] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [justification, setJustification] = useState("");
  const [completionDescription, setCompletionDescription] = useState("");
  const [completionResponse, setCompletionResponse] = useState("");
  const [completionPhotos, setCompletionPhotos] = useState<any[]>([]);

  // Mutations
  const startMutation = useStartServiceOrder();
  const suspendMutation = useSuspendServiceOrder();
  const resumeMutation = useResumeServiceOrder();
  const completeMutation = useCompleteServiceOrder();
  const returnMutation = useReturnServiceOrder();
  const cancelMutation = useCancelServiceOrder();
  const createCommentMutation = useCreateOccurrenceComment();

  // Handlers
  const handleStart = async () => {
    try {
      await startMutation.mutateAsync(id!);
      toast.success("Ordem de serviço iniciada com sucesso!");
      refetch();
      refetchHistory();
    } catch (error) {
      toast.error("Erro ao iniciar ordem de serviço");
    }
  };

  const handleResume = async () => {
    try {
      await resumeMutation.mutateAsync(id!);
      toast.success("Ordem de serviço retomada com sucesso!");
      refetch();
      refetchHistory();
    } catch (error) {
      toast.error("Erro ao retomar ordem de serviço");
    }
  };

  const handleComplete = () => {
    setCompleteDialogOpen(true);
  };

  const handleCompleteWithDetails = async () => {
    if (!completionDescription.trim()) {
      toast.error("Por favor, descreva o serviço realizado");
      return;
    }

    if (!completionResponse.trim()) {
      toast.error("Por favor, informe a resposta ao cidadão");
      return;
    }

    try {
      // Create formatted comment with completion details
      const formattedMessage = `Prezado(a) cidadão(ã),

Informamos que o serviço solicitado foi concluído com sucesso.

Serviço realizado:
${completionDescription}

${completionResponse}

Atenciosamente,
Equipe de Atendimento`;

      // Create public comment for all occurrences in the service order items
      if (serviceOrder?.items && serviceOrder.items.length > 0) {
        console.log(
          `Criando comentário para ${serviceOrder.items.length} ocorrência(s)`,
        );

        // Create comment for each occurrence in items
        for (const item of serviceOrder.items) {
          if (item.occurrence?.id) {
            console.log(
              "Criando comentário para ocorrência:",
              item.occurrence.id,
            );
            await createCommentMutation.mutateAsync({
              occurrenceId: item.occurrence.id,
              type: "PUBLIC",
              content: formattedMessage,
              attachments: completionPhotos,
            });
            console.log(
              "Comentário criado com sucesso para ocorrência:",
              item.occurrence.id,
            );
          }
        }
      } else {
        console.warn("Ordem de serviço não possui ocorrências vinculadas");
        toast(
          "Ordem de serviço não possui ocorrências vinculadas. Apenas finalizando a ordem.",
          { icon: "⚠️" },
        );
      }

      // Complete service order
      console.log("Finalizando ordem de serviço:", id);
      await completeMutation.mutateAsync(id!);
      console.log("Ordem de serviço finalizada");

      toast.success("Ordem de serviço completada com sucesso!");
      setCompleteDialogOpen(false);
      setCompletionDescription("");
      setCompletionResponse("");
      setCompletionPhotos([]);
      refetch();
      refetchHistory();
    } catch (error: any) {
      console.error("Erro ao finalizar ordem de serviço:", error);
      toast.error(
        error?.response?.data?.message || "Erro ao finalizar ordem de serviço",
      );
    }
  };

  const handleSuspend = async () => {
    if (!reason.trim()) {
      toast.error("Por favor, informe o motivo da suspensão");
      return;
    }
    try {
      await suspendMutation.mutateAsync({ id: id!, reason });
      toast.success("Ordem de serviço suspensa com sucesso!");
      setSuspendDialogOpen(false);
      setReason("");
      refetch();
      refetchHistory();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erro ao suspender ordem de serviço");
    }
  };

  const handleCancel = async () => {
    if (!justification.trim()) {
      toast.error("Por favor, informe a justificativa do cancelamento");
      return;
    }
    try {
      await cancelMutation.mutateAsync({ id: id!, reason: justification });
      toast.success("Ordem de serviço cancelada com sucesso!");
      setCancelDialogOpen(false);
      setJustification("");
      refetch();
      refetchHistory();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erro ao cancelar ordem de serviço");
    }
  };

  const handleReturn = async () => {
    if (!reason.trim()) {
      toast.error("Por favor, informe o motivo do retorno");
      return;
    }
    try {
      await returnMutation.mutateAsync({ id: id!, reason });
      toast.success("Ordem de serviço retornada à ouvidoria com sucesso!");
      setReturnDialogOpen(false);
      setReason("");
      refetch();
      refetchHistory();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erro ao retornar ordem de serviço");
    }
  };

  const handleDuplicate = () => {
    // Navega para a tela de criação com os dados da OS atual
    navigate("/service-orders/create", {
      state: {
        duplicateData: {
          title: `[Cópia] ${serviceOrder.title}`,
          description: serviceOrder.description,
          priority: serviceOrder.priority,
          sectorId: serviceOrder.sectorId,
          assignedToId: serviceOrder.assignedToId,
          assignedTeamId: serviceOrder.assignedTeamId,
          estimatedStartDate: serviceOrder.estimatedStartDate,
          estimatedEndDate: serviceOrder.estimatedEndDate,
        },
      },
    });
  };

  // Função para determinar quais botões mostrar baseado no status
  const getAvailableActions = () => {
    const status = serviceOrder?.status;
    const actions = [];

    if (status === "PENDING") {
      actions.push(
        <Button
          key="start"
          onClick={handleStart}
          className="bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
          size="sm"
        >
          <Play className="mr-2 h-4 w-4" />
          Iniciar
        </Button>,
      );
    }

    if (status === "IN_PROGRESS") {
      actions.push(
        <Button
          key="suspend"
          onClick={() => setSuspendDialogOpen(true)}
          className="bg-yellow-600 hover:bg-yellow-700 text-white cursor-pointer"
          size="sm"
        >
          <Pause className="mr-2 h-4 w-4" />
          Suspender
        </Button>,
        <Button
          key="complete"
          onClick={handleComplete}
          className="bg-green-600 hover:bg-green-700 text-white cursor-pointer"
          size="sm"
        >
          <CheckCircle className="mr-2 h-4 w-4" />
          Finalizar
        </Button>,
      );
    }

    if (status === "SUSPENDED") {
      actions.push(
        <Button
          key="resume"
          onClick={handleResume}
          className="bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
          size="sm"
        >
          <Play className="mr-2 h-4 w-4" />
          Retomar
        </Button>,
      );
    }

    if (["PENDING", "IN_PROGRESS", "SUSPENDED"].includes(status)) {
      actions.push(
        <Button
          key="return"
          onClick={() => setReturnDialogOpen(true)}
          className="bg-orange-600 hover:bg-orange-700 text-white cursor-pointer"
          size="sm"
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          Retornar à Ouvidoria
        </Button>,
        <Button
          key="cancel"
          onClick={() => setCancelDialogOpen(true)}
          className="bg-red-600 hover:bg-red-700 text-white cursor-pointer"
          size="sm"
        >
          <XCircle className="mr-2 h-4 w-4" />
          Cancelar
        </Button>,
      );
    }

    return actions;
  };

  if (isLoading) {
    return <Loading label="Carregando ordem de serviço..." />;
  }

  if (error || !serviceOrder) {
    return (
      <div className="flex flex-col h-full bg-gray-50">
        <HeaderPage
          title="Visualizar Ordem de Serviço"
          description="Detalhes da ordem de serviço"
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-500 mb-4">
              {error
                ? "Erro ao carregar ordem de serviço"
                : "Ordem de serviço não encontrada"}
            </p>
            <Button onClick={() => navigate("/service-orders")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar para Ordens de Serviço
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="flex items-start justify-between gap-4">
        <HeaderPage
          title="Visualizar Ordem de Serviço"
          description="Detalhes da ordem de serviço"
        />

        {/* Botões de Ação */}
        <div className="flex flex-wrap gap-2 items-start">
          {getAvailableActions()}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 overflow-auto mt-10"
      >
        {/* Botões de Navegação */}
        <div className="flex gap-3 mb-6">
          <Button
            onClick={() => navigate("/service-orders")}
            variant="outline"
            className="cursor-pointer"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          <Button
            onClick={() => navigate(`/service-orders/edit/${id}`)}
            variant="outline"
            className="cursor-pointer"
          >
            <Edit className="mr-2 h-4 w-4" />
            Editar
          </Button>
          <Button
            onClick={handleDuplicate}
            variant="outline"
            className="cursor-pointer"
          >
            <Copy className="mr-2 h-4 w-4" />
            Duplicar
          </Button>
        </div>

        <div className="mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conteúdo Principal */}
          <div className="lg:col-span-2 flex min-w-0">
            <Card className="bg-white flex-1 min-w-0">
              <CardContentUI className="p-6 min-w-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 overflow-hidden">
                  {/* Número da OS */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">
                      Número da Ordem de Serviço
                    </label>
                    <p className="text-base font-semibold text-primary">
                      {serviceOrder.osNumber}
                    </p>
                  </div>

                  {/* Título */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">
                      Título
                    </label>
                    <p className="text-base font-semibold break-words">
                      {serviceOrder.title}
                    </p>
                  </div>

                  {/* Prioridade */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">
                      Prioridade
                    </label>
                    <div>
                      <Badge
                        variant="default"
                        className={
                          priorityLabels[serviceOrder.priority]?.color ||
                          "bg-gray-500"
                        }
                      >
                        {priorityLabels[serviceOrder.priority]?.label ||
                          serviceOrder.priority}
                      </Badge>
                    </div>
                  </div>

                  {/* Setor */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">
                      Setor
                    </label>
                    <p className="text-base font-semibold break-words">
                      {serviceOrder.sector?.name || "N/A"}
                    </p>
                  </div>

                  {/* Status */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">
                      Status
                    </label>
                    <div>
                      <Badge
                        className={
                          statusLabels[serviceOrder.status]?.color ||
                          "bg-gray-500"
                        }
                      >
                        {statusLabels[serviceOrder.status]?.label ||
                          serviceOrder.status}
                      </Badge>
                    </div>
                  </div>

                  {/* Responsável */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">
                      Responsável
                    </label>
                    <p className="text-base font-semibold break-words">
                      {serviceOrder.assignedTo?.profile?.fullName ||
                        serviceOrder.assignedTo?.email ||
                        "N/A"}
                    </p>
                  </div>

                  {/* Equipe */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">
                      Equipe
                    </label>
                    <p className="text-base font-semibold break-words">
                      {serviceOrder.assignedTeam?.name || "N/A"}
                    </p>
                  </div>

                  {/* Data de Início */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">
                      {serviceOrder.actualStartDate
                        ? "Data de Início Real"
                        : "Data de Início Estimada"}
                    </label>
                    <p className="text-base font-semibold">
                      {serviceOrder.actualStartDate
                        ? new Date(serviceOrder.actualStartDate).toLocaleString(
                            "pt-BR",
                          )
                        : serviceOrder.estimatedStartDate
                          ? new Date(
                              serviceOrder.estimatedStartDate,
                            ).toLocaleString("pt-BR")
                          : "N/A"}
                    </p>
                    {serviceOrder.actualStartDate &&
                      serviceOrder.estimatedStartDate && (
                        <p className="text-sm text-gray-400">
                          Estimada:{" "}
                          {new Date(
                            serviceOrder.estimatedStartDate,
                          ).toLocaleString("pt-BR")}
                        </p>
                      )}
                  </div>

                  {/* Data de Término */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">
                      {serviceOrder.actualEndDate
                        ? "Data de Término Real"
                        : "Data de Término Estimada"}
                    </label>
                    <p className="text-base font-semibold">
                      {serviceOrder.actualEndDate
                        ? new Date(serviceOrder.actualEndDate).toLocaleString(
                            "pt-BR",
                          )
                        : serviceOrder.estimatedEndDate
                          ? new Date(
                              serviceOrder.estimatedEndDate,
                            ).toLocaleString("pt-BR")
                          : "N/A"}
                    </p>
                    {serviceOrder.actualEndDate &&
                      serviceOrder.estimatedEndDate && (
                        <p className="text-sm text-gray-400">
                          Estimada:{" "}
                          {new Date(
                            serviceOrder.estimatedEndDate,
                          ).toLocaleString("pt-BR")}
                        </p>
                      )}
                  </div>

                  {/* Data de Criação */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">
                      Data de Criação
                    </label>
                    <p className="text-base font-semibold">
                      {new Date(serviceOrder.createdAt).toLocaleString("pt-BR")}
                    </p>
                  </div>

                  {/* Criado Por */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">
                      Criado Por
                    </label>
                    <p className="text-base font-semibold break-words">
                      {serviceOrder.createdBy?.email || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Descrição */}
                <div className="mt-6 space-y-2">
                  <label className="text-sm font-medium text-gray-500">
                    Descrição
                  </label>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-base whitespace-pre-wrap break-words">
                      {serviceOrder.description}
                    </p>
                  </div>
                </div>

                {/* Ocorrências Relacionadas */}
                <div className="mt-6 space-y-2">
                  <label className="text-sm font-medium text-gray-500">
                    Ocorrências Relacionadas
                  </label>
                  <div className="border rounded-lg overflow-hidden overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-32">Protocolo</TableHead>
                          <TableHead className="w-40">Categoria</TableHead>
                          <TableHead>Descrição</TableHead>
                          <TableHead className="w-20 text-center">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {serviceOrder.items && serviceOrder.items.length > 0 ? (
                          serviceOrder.items.map((item: any) => (
                            <TableRow 
                              key={item.id}
                              className="hover:bg-gray-50 cursor-pointer"
                              onClick={() => {
                                setSelectedOccurrenceProtocol(item.occurrence?.protocol);
                                setViewOccurrenceDialogOpen(true);
                              }}
                            >
                              <TableCell className="font-medium">
                                {item.occurrence?.protocol || "Sem protocolo"}
                              </TableCell>
                              <TableCell>
                                {item.occurrence?.category?.name || "N/A"}
                              </TableCell>
                              <TableCell className="max-w-xs break-words">
                                {item.occurrence?.description?.substring(
                                  0,
                                  100,
                                ) || "Sem descrição"}
                                ...
                              </TableCell>
                              <TableCell className="text-center">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedOccurrenceProtocol(item.occurrence?.protocol);
                                    setViewOccurrenceDialogOpen(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={3}
                              className="text-center text-gray-500"
                            >
                              Nenhuma ocorrência relacionada
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContentUI>
            </Card>
          </div>

          {/* Sidebar - Histórico */}
          <div className="lg:col-span-1 flex">
            <Card className="bg-white flex-1">
              <CardContentUI className="p-6 flex flex-col h-full">
                <h3 className="text-lg font-semibold mb-4">Histórico</h3>
                {history.length > 0 ? (
                  <div className="space-y-3 overflow-y-auto flex-1">
                    {history.map((item: any) => {
                      const actionLabels: Record<string, string> = {
                        created: "Criada",
                        status_changed: "Status alterado",
                        priority_changed: "Prioridade alterada",
                        assigned_changed: "Responsável alterado",
                        completed: "Concluída",
                        suspended: "Suspensa",
                        resumed: "Retomada",
                        cancelled: "Cancelada",
                        returned: "Retornada à Ouvidoria",
                        sent_to_validation: "Enviada para Validação",
                        evaluation_rejected: "Avaliação Rejeitada",
                        validated: "Validada",
                      };

                      return (
                        <div
                          key={item.id}
                          className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <Clock className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-gray-900">
                                  {actionLabels[item.action] || item.action}
                                </p>
                                {item.action === "status_changed" && (
                                  <div className="flex items-center gap-1.5 text-xs mt-1 flex-wrap">
                                    <Badge
                                      className={`text-xs py-0 ${statusLabels[item.fromValue]?.color || "bg-gray-500"}`}
                                    >
                                      {statusLabels[item.fromValue]?.label ||
                                        item.fromValue}
                                    </Badge>
                                    <span className="text-gray-400">→</span>
                                    <Badge
                                      className={`text-xs py-0 ${statusLabels[item.toValue]?.color || "bg-gray-500"}`}
                                    >
                                      {statusLabels[item.toValue]?.label ||
                                        item.toValue}
                                    </Badge>
                                  </div>
                                )}
                                {item.justification && (
                                  <p className="text-xs text-gray-600 mt-1">
                                    <span className="font-medium">
                                      Justificativa:
                                    </span>{" "}
                                    {item.justification}
                                  </p>
                                )}
                                <div className="flex flex-col gap-1 text-xs text-gray-500 mt-2">
                                  <span>
                                    {new Date(item.performedAt).toLocaleString(
                                      "pt-BR",
                                    )}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {item.performer?.profile?.fullName ||
                                      item.performer?.email ||
                                      item.performer?.id}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Nenhum histórico disponível.
                  </p>
                )}
              </CardContentUI>
            </Card>
          </div>
        </div>
      </motion.div>

      {/* Dialog de Suspensão */}
      <Dialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspender Ordem de Serviço</DialogTitle>
            <DialogDescription>
              A ordem de serviço será suspensa temporariamente. Informe o motivo para documentação e acompanhamento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Motivo da Suspensão <span className="text-red-500">*</span>
            </label>
            <TextArea
              placeholder="Ex: Aguardando material, condições climáticas desfavoráveis, falta de equipe..."
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <p className="text-xs text-gray-500">
              Esta ordem poderá ser retomada posteriormente.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSuspendDialogOpen(false);
                setReason("");
              }}
              className="cursor-pointer"
              disabled={suspendMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSuspend}
              className="bg-yellow-600 hover:bg-yellow-700 text-white cursor-pointer"
              disabled={suspendMutation.isPending || !reason.trim()}
            >
              {suspendMutation.isPending ? "Suspendendo..." : "Confirmar Suspensão"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Cancelamento */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Ordem de Serviço</DialogTitle>
            <DialogDescription>
              ⚠️ Esta ação cancelará definitivamente a ordem de serviço e liberará todas as ocorrências vinculadas. Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Justificativa do Cancelamento <span className="text-red-500">*</span>
            </label>
            <TextArea
              placeholder="Ex: Falta de recursos, mudança de prioridades, demanda cancelada pelo solicitante..."
              rows={4}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
            />
            <p className="text-xs text-red-600 font-medium">
              As ocorrências vinculadas retornarão ao status AWAITING_TRIAGE.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCancelDialogOpen(false);
                setJustification("");
              }}
              className="cursor-pointer"
              disabled={cancelMutation.isPending}
            >
              Voltar
            </Button>
            <Button
              onClick={handleCancel}
              variant="destructive"
              disabled={cancelMutation.isPending || !justification.trim()}
              className="cursor-pointer"
            >
              {cancelMutation.isPending ? "Cancelando..." : "Confirmar Cancelamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Retorno à Ouvidoria */}
      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Retornar à Ouvidoria</DialogTitle>
            <DialogDescription>
              Esta ordem será devolvida à ouvidoria para reanálise. As ocorrências vinculadas serão liberadas para nova triagem.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Motivo do Retorno <span className="text-red-500">*</span>
            </label>
            <TextArea
              placeholder="Ex: Ocorrências fora do escopo, necessidade de reavaliação, informações incompletas..."
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <p className="text-xs text-orange-600 font-medium">
              As ocorrências retornarão ao status AWAITING_TRIAGE para nova análise.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setReturnDialogOpen(false);
                setReason("");
              }}
              className="cursor-pointer"
              disabled={returnMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleReturn}
              className="bg-orange-600 hover:bg-orange-700 text-white cursor-pointer"
              disabled={returnMutation.isPending || !reason.trim()}
            >
              {returnMutation.isPending ? "Retornando..." : "Confirmar Retorno"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Visualização de Ocorrência */}
      <Dialog open={viewOccurrenceDialogOpen} onOpenChange={(open) => {
        setViewOccurrenceDialogOpen(open);
        if (!open) setSelectedOccurrenceProtocol(null);
      }}>
        <DialogContent 
          className="!max-w-[85vw] w-full h-[90vh] p-0 overflow-hidden"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="h-full overflow-y-auto bg-gray-50">
            {selectedOccurrenceProtocol && (
              <OccurrenceDetails protocolProp={selectedOccurrenceProtocol} />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Conclusão */}
      <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Finalizar Ordem de Serviço</DialogTitle>
            <DialogDescription>
              Informe os dados relacionados à conclusão da ordem de serviço.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Descrição do Serviço Realizado{" "}
                <span className="text-red-500">*</span>
              </label>
              <TextArea
                placeholder="Descreva o serviço que foi realizado..."
                rows={4}
                value={completionDescription}
                onChange={(e) => setCompletionDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Fotos de Conclusão</label>
              <FileUpload onFilesChange={setCompletionPhotos} />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Resposta ao Cidadão <span className="text-red-500">*</span>
              </label>
              <TextArea
                placeholder="Digite a resposta que será enviada ao cidadão..."
                rows={4}
                value={completionResponse}
                onChange={(e) => setCompletionResponse(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCompleteDialogOpen(false);
                setCompletionDescription("");
                setCompletionResponse("");
                setCompletionPhotos([]);
              }}
              className="cursor-pointer"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCompleteWithDetails}
              className="cursor-pointer text-white"
              disabled={
                completeMutation.isPending || createCommentMutation.isPending
              }
            >
              {completeMutation.isPending || createCommentMutation.isPending
                ? "Finalizando..."
                : "Finalizar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Componente de página que usa o ServiceOrderDetails
export const ViewServiceOrder = () => {
  return <ServiceOrderDetails />;
};
